import { EVENTS_BY_ACTIONS_MAP, SESSION_PROPERTIES, EVENTS } from 'shared/constants';
import {
  AUTH_UPDATE_USER,
  AUTH_UPDATE_AUTH_CONTEXT,
  ORGANIZATION_UPDATE,
  AUTH_LOGIN,
  FIRST_TIME,
  AUTH_LOGOUT,
} from 'shared/store/actions';
import { getUser, getUserId } from 'shared/store/selectors/user.selector';
import AnalyticsService from '../../helpers/analytics.service';
import monitorService from '../../helpers/monitor.service';

export const analyticsByAction = () => next => async action => {
  next(action);

  // AUTH_LOGOUT is tracked explicitly in analyticsLogout so it can be sent
  // before reset() rotates the Mixpanel identity — see the comment there.
  if (action.type !== AUTH_LOGOUT && EVENTS_BY_ACTIONS_MAP[action.type]) {
    AnalyticsService.track(EVENTS_BY_ACTIONS_MAP[action.type]);
  }
};

export const analyticsEnricher = () => next => async action => {
  next(action);

  if (action.type === FIRST_TIME) {
    AnalyticsService.setSessionProperty(SESSION_PROPERTIES.IS_FIRST_TIME, true);
  }
};

export const analyticsAddUserId =
  ({ getState }) =>
  next =>
  async action => {
    next(action);

    if (action.type === AUTH_UPDATE_USER || action.type === AUTH_LOGIN) {
      const state = getState();
      let user = getUser(state);
      const userId = getUserId(state);

      if (userId) {
        monitorService.grantConsent();
        await AnalyticsService.grantConsent();
      }

      const userData = { ...user, ...user?.professional, id: userId };
      const propertiesToIgnore = [
        'professional',
        'organizationId',
        'snooze',
        'createdAt',
        'updatedAt',
        'profileCompletedAt',
        'type',
      ];
      AnalyticsService.setSessionProperty(SESSION_PROPERTIES.USER_TYPE, user?.type);

      propertiesToIgnore.forEach(property => {
        delete userData[property];
      });

      AnalyticsService.setUserId(userId);
      AnalyticsService.registerUserData(userData);

      AnalyticsService.userReady();

      if (window.StonlyWidget) {
        window.StonlyWidget('identify', userId, {
          permissions: user?.permissions,
          organizationId: user?.organizationId,
          createdAt: user?.createdAt,
        });
      }
    }
  };

export const analyticsAddOrgId = () => next => async action => {
  next(action);

  if (action.type === ORGANIZATION_UPDATE) {
    AnalyticsService.setGroup({
      key: 'organization',
      value: action.payload?.name,
    });
    AnalyticsService.setGroup({
      key: 'organizationId',
      value: action.payload?.id,
    });
    AnalyticsService.setGroup({
      key: 'organizationClassification',
      value: action.payload?.classification,
    });
  }
};

export const analyticsAuthContext = () => next => async action => {
  next(action);

  if (action.type === AUTH_UPDATE_AUTH_CONTEXT) {
    AnalyticsService.track(EVENTS.AUTH.MAGIC_LINK_TRIGGERED);
  }
};

export const analyticsLogout =
  ({ getState }) =>
  next =>
  action => {
    if (action.type === AUTH_LOGOUT) {
      // Read identity BEFORE next(action) clears the auth slice.
      const userId = getUserId(getState());

      // AUTH_LOGOUT can fire twice: a user-initiated logout, then a
      // TOKEN_REFRESH_FAILED forced logout from a 401 racing it. The first
      // dispatch clears the auth slice, so by the second there's no identity
      // left — skip tracking to avoid an anonymized duplicate '[Auth] Logout'.
      if (userId) {
        // mixpanel-browser batches events (5s flush) and stamps the user's
        // distinct_id at flush time — but reset() rotates that id
        // synchronously, so a batched '[Auth] Logout' would ship with an
        // anonymous id (dropping user_id/organization_id). Send it immediately
        // with identity intact and reset only once it's away, so logout carries
        // the same user/org/group properties every other event does (matching
        // mobile).
        AnalyticsService.track(
          EVENTS_BY_ACTIONS_MAP[AUTH_LOGOUT],
          {},
          { send_immediately: true },
          () => AnalyticsService.reset(),
        );
      } else {
        AnalyticsService.reset();
      }
    }

    next(action);
  };

export const analyticsMiddleware = [
  analyticsByAction,
  analyticsEnricher,
  analyticsAddUserId,
  analyticsAddOrgId,
  analyticsAuthContext,
  analyticsLogout,
];
